import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';
import { CategoryPicker } from './CategoryPicker';
import { PrioritySelector } from './PrioritySelector';
import { ConfirmModal } from './ConfirmModal';
import { useUser } from '../contexts/UserContext';
interface Goal {
  id: string;
  title: string;
  category: 'Work' | 'Study' | 'Personal' | 'Health';
  priority: 'High' | 'Medium' | 'Low';
  dueDate?: string;
  dueTime?: string;
}
interface EditGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
  goal: Goal | null;
}
export function EditGoalModal({
  isOpen,
  onClose,
  goal
}: EditGoalModalProps) {
  const {
    updateGoal
  } = useUser();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<'Work' | 'Study' | 'Personal' | 'Health'>('Work');
  const [priority, setPriority] = useState<'High' | 'Medium' | 'Low'>('Medium');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  // Populate form when goal changes
  useEffect(() => {
    if (goal) {
      setTitle(goal.title);
      setCategory(goal.category);
      setPriority(goal.priority);
      setDueDate(goal.dueDate || '');
      setDueTime(goal.dueTime || '');
    }
  }, [goal]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!goal) return;
    // Validation
    if (!title.trim()) {
      setValidationMessage('Please enter a quest title');
      setShowValidationModal(true);
      return;
    }
    if (!dueDate) {
      setValidationMessage('Please select a due date for your quest');
      setShowValidationModal(true);
      return;
    }
    if (!dueTime) {
      setValidationMessage('Please select a due time for your quest');
      setShowValidationModal(true);
      return;
    }
    setLoading(true);
    try {
      await updateGoal(goal.id, {
        title: title.trim(),
        category,
        priority,
        dueDate,
        dueTime
      });
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  if (!goal) return null;
  return <>
      <AnimatePresence data-id="element-216">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose} data-id="element-217">
            <motion.div initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} className="bg-white dark:bg-[#252936] rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-218">
              {/* Header */}
              <div className="flex items-center justify-between mb-6" data-id="element-219">
                <h2 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-220">
                  Edit Quest
                </h2>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-221">
                  <X className="w-6 h-6 text-gray-500 dark:text-gray-400" data-id="element-222" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-223">
                <Input label="Quest Title" placeholder="What do you want to accomplish?" value={title} onChange={e => setTitle(e.target.value)} required data-id="element-224" />

                <div data-id="element-225">
                  <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-226">
                    Category
                  </label>
                  <CategoryPicker selected={category} onSelect={setCategory} data-id="element-227" />
                </div>

                <div data-id="element-228">
                  <label className="block text-sm font-quicksand font-bold text-gray-700 dark:text-gray-300 mb-3" data-id="element-229">
                    Priority
                  </label>
                  <PrioritySelector selected={priority} onSelect={setPriority} data-id="element-230" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-231">
                  <Input label="Due Date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} required min={new Date().toISOString().split('T')[0]} data-id="element-232" />
                  <Input label="Due Time" type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} required data-id="element-233" />
                </div>

                <div className="flex gap-3 pt-4" data-id="element-234">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-235">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading} data-id="element-236">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <ConfirmModal isOpen={showValidationModal} onClose={() => setShowValidationModal(false)} onConfirm={() => setShowValidationModal(false)} title="Missing Information" message={validationMessage} confirmText="Got it" cancelText="" variant="warning" data-id="element-237" />
    </>;
}